import i81n from "i18next";
import { DateTime } from "luxon";

/** A date value in any supported input form. */
export type RawDate = string | Date | DateTime;

/** Parse a date string in yyyy-MM-dd, "MMMM d, yyyy", or ISO format. */
const parseDateString = (dateString: string): Date => {
	const ymd = DateTime.fromFormat(dateString, "yyyy-MM-dd");
	if (ymd.isValid) {
		return ymd.toJSDate();
	}

	const mdy = DateTime.fromFormat(dateString, "MMMM d, yyyy");
	if (mdy.isValid) {
		return mdy.toJSDate();
	}

	const normalized = dateString.replace(" ", "T").replace(/\+00$/, "Z");
	const fromIso = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(normalized)
		? DateTime.fromISO(normalized, { zone: "utc" })
		: DateTime.fromISO(normalized);
	if (fromIso.isValid) {
		return fromIso.toJSDate();
	}

	return new Date(normalized);
};

/** Convert a raw date value to a valid `Date`, or `undefined` if missing or invalid. */
export const normalizeDate = (rawDate?: RawDate | null): Date | undefined => {
	if (!rawDate) {
		return undefined;
	}

	const jsDate =
		typeof rawDate === "string"
			? parseDateString(rawDate)
			: rawDate instanceof Date
				? rawDate
				: rawDate.toJSDate();

	if (Number.isNaN(jsDate.getTime())) {
		return undefined;
	}

	return jsDate;
};

/** Returns true when the date is exactly midnight UTC. */
export const isUtcMidnight = (date: Date) =>
	date.getUTCHours() === 0 &&
	date.getUTCMinutes() === 0 &&
	date.getUTCSeconds() === 0 &&
	date.getUTCMilliseconds() === 0;

/** Strip time and return a local calendar date (midnight in the local timezone). */
export const toLocalCalendarDate = (
	rawDate?: RawDate | null,
): Date | undefined => {
	const jsDate = normalizeDate(rawDate);
	if (!jsDate) {
		return undefined;
	}

	const utcMidnight = isUtcMidnight(jsDate);
	const year = utcMidnight ? jsDate.getUTCFullYear() : jsDate.getFullYear();
	const month = utcMidnight ? jsDate.getUTCMonth() : jsDate.getMonth();
	const day = utcMidnight ? jsDate.getUTCDate() : jsDate.getDate();

	return new Date(year, month, day);
};

/**
 * Clamp a DateTime to today (local start of day) when it falls in the past;
 * otherwise return the same instant as a `Date`.
 */
export const clampToTodayOrLater = (date: DateTime): Date => {
	const today = DateTime.local().startOf("day");
	return date < today ? today.toJSDate() : date.toJSDate();
};

/** Today's date as a `yyyy-MM-dd` string in the local timezone. */
export const getCurrentDateKey = () => DateTime.local().toFormat("yyyy-MM-dd");

/** Calendar days from today until the given date (negative if in the past). */
export const getCalendarDaysUntil = (
	rawDate?: RawDate | null,
): number | undefined => {
	const jsDate = normalizeDate(rawDate);
	if (!jsDate) {
		return undefined;
	}

	const examDay = isUtcMidnight(jsDate)
		? DateTime.local(
				jsDate.getUTCFullYear(),
				jsDate.getUTCMonth() + 1,
				jsDate.getUTCDate(),
			)
		: DateTime.fromJSDate(jsDate);

	const target = examDay.startOf("day");
	const today = DateTime.local().startOf("day");
	return Math.floor(target.diff(today, "days").days);
};

/** Format a date as a locale-aware date string (default: "June 15, 2026"). */
export const formatDate = (
	rawDate: RawDate,
	locales?: Intl.LocalesArgument,
	options?: Intl.DateTimeFormatOptions,
): string => {
	const date = normalizeDate(rawDate);
	if (!date) {
		return typeof rawDate === "string" ? rawDate : "";
	}

	return date.toLocaleString(locales ?? i81n.language, {
		year: "numeric",
		month: "long",
		day: "numeric",
		...options,
	});
};

const capitalize = (text: string, locales?: Intl.LocalesArgument): string =>
	text.charAt(0).toLocaleUpperCase(locales as string | string[]) +
	text.slice(1);

/**
 * Format a date naturally relative to today.
 * Returns undefined when no date is provided.
 */
export const formatNaturalDate = (
	rawDate?: RawDate | null,
	locales?: Intl.LocalesArgument,
	now: Date = new Date(),
): string | undefined => {
	if (!rawDate) {
		return undefined;
	}

	const date = normalizeDate(rawDate);
	if (!date) {
		return typeof rawDate === "string" ? rawDate : "";
	}

	const resolvedLocales = locales ?? i81n.language;
	const day = DateTime.fromJSDate(date).startOf("day");
	const today = DateTime.fromJSDate(now).startOf("day");
	const dayDiff = Math.round(day.diff(today, "days").days);

	if (
		(dayDiff === 0 || dayDiff === -1) &&
		typeof Intl.RelativeTimeFormat === "function"
	) {
		const relative = new Intl.RelativeTimeFormat(
			resolvedLocales as string | string[] | undefined,
			{ numeric: "auto" },
		).format(dayDiff, "day");
		return capitalize(relative, resolvedLocales);
	}
	if (dayDiff < 0 && dayDiff > -7) {
		return date.toLocaleString(resolvedLocales, { weekday: "long" });
	}
	return date.toLocaleString(resolvedLocales, {
		day: "numeric",
		month: "long",
		...(day.year === today.year ? {} : { year: "numeric" }),
	});
};

/** Beyond this many days out, an exam reads as a calendar date rather than a countdown. */
const EXAM_COUNTDOWN_MAX_DAYS = 30;

/**
 * Format an exam date as a full calendar date ("June 16, 2026"), or with the
 * year dropped inside the current year when `omitCurrentYear` is set.
 *
 * Exam dates arrive as UTC midnight, so the local calendar day is resolved
 * first: formatting the raw instant renders the previous day for users behind
 * UTC, which would contradict {@link formatExamDateStatValue}.
 * Returns undefined when no date is provided.
 */
export const formatExamDate = (
	rawDate?: RawDate | null,
	locales?: Intl.LocalesArgument,
	{
		omitCurrentYear = false,
		now = new Date(),
	}: { omitCurrentYear?: boolean; now?: Date } = {},
): string | undefined => {
	if (!rawDate) {
		return undefined;
	}

	const date = toLocalCalendarDate(rawDate);
	if (!date) {
		return typeof rawDate === "string" ? rawDate : "";
	}

	const hideYear = omitCurrentYear && date.getFullYear() === now.getFullYear();
	return formatDate(date, locales, hideYear ? { year: undefined } : undefined);
};

/**
 * Format an exam date for the progress stat tile: "Today", "Tomorrow" and
 * "In 12 days" while the exam is near, switching to a calendar date
 * ("September 30", or "September 30, 2027" outside the current year) once it
 * is further out than 30 days or already past.
 *
 * Hermes builds without full ICU omit `Intl.RelativeTimeFormat`, so the
 * calendar date doubles as the fallback there.
 * Returns undefined when no date is provided.
 */
export const formatExamDateStatValue = (
	rawDate?: RawDate | null,
	locales?: Intl.LocalesArgument,
	now: Date = new Date(),
): string | undefined => {
	if (!rawDate) {
		return undefined;
	}

	const date = toLocalCalendarDate(rawDate);
	if (!date) {
		return typeof rawDate === "string" ? rawDate : "";
	}

	const resolvedLocales = locales ?? i81n.language;
	const day = DateTime.fromJSDate(date).startOf("day");
	const today = DateTime.fromJSDate(now).startOf("day");
	const dayDiff = Math.round(day.diff(today, "days").days);

	if (
		dayDiff < 0 ||
		dayDiff > EXAM_COUNTDOWN_MAX_DAYS ||
		typeof Intl.RelativeTimeFormat !== "function"
	) {
		return formatExamDate(date, resolvedLocales, {
			omitCurrentYear: true,
			now,
		});
	}

	const relative = new Intl.RelativeTimeFormat(
		resolvedLocales as string | string[] | undefined,
		{ numeric: "auto" },
	).format(dayDiff, "day");

	return capitalize(relative, resolvedLocales);
};

/** Format a date as a locale-aware time string (default: 12-hour with minutes). */
export const formatTime = (
	rawDate: RawDate,
	locales?: Intl.LocalesArgument,
	options?: Intl.DateTimeFormatOptions,
) => {
	const date = normalizeDate(rawDate);
	if (!date) {
		return typeof rawDate === "string" ? rawDate : "";
	}

	return date.toLocaleString(locales ?? i81n.language, {
		hour: "numeric",
		minute: "numeric",
		hour12: true,
		...options,
	});
};

/** Format a date as a locale-aware date and time string. */
export const formatDateTime = (
	rawDate: RawDate,
	locales?: Intl.LocalesArgument,
	options?: Intl.DateTimeFormatOptions,
) => {
	const date = normalizeDate(rawDate);
	if (!date) {
		return typeof rawDate === "string" ? rawDate : "";
	}

	return date.toLocaleString(locales ?? i81n.language, {
		year: "numeric",
		month: "long",
		day: "numeric",
		hour: "numeric",
		minute: "numeric",
		hour12: true,
		...options,
	});
};
